import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
// Old AdminHeader deleted - use DashboardLayout with topnav instead
import AdminSidebar from '../sidebar';
import AdminServices from '../../../services/AdminServices';

const AdminTeacherQuizDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    accountType: 'all',
    certificationStatus: 'all',
    renewalDue: 'all'
  });
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await AdminServices.getTeachersQuizStatus();
        setData(result);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard data');
        console.error('Error fetching teachers quiz status:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredTeachers = useMemo(() => {
    if (!data?.teachers) return [];

    let filtered = [...data.teachers];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(teacher =>
        teacher.name?.toLowerCase().includes(term) ||
        teacher.email?.toLowerCase().includes(term) ||
        teacher.school?.name?.toLowerCase().includes(term)
      );
    }

    // Account type filter
    if (filters.accountType !== 'all') {
      filtered = filtered.filter(teacher =>
        filters.accountType === 'school'
          ? teacher.accountType === 'School Teacher'
          : teacher.accountType === 'Individual Teacher'
      );
    }

    // Certification status filter
    if (filters.certificationStatus !== 'all') {
      filtered = filtered.filter(teacher => {
        switch (filters.certificationStatus) {
          case 'completed':
            return teacher.certifications?.completed > 0;
          case 'inProgress':
            return teacher.certifications?.inProgress > 0;
          case 'expired':
            return teacher.certifications?.expired > 0;
          case 'none':
            return teacher.certifications?.total === 0;
          default:
            return true;
        }
      });
    }

    // Renewal due filter
    if (filters.renewalDue !== 'all') {
      filtered = filtered.filter(teacher => {
        const renewal = teacher.certifications?.earliestRenewal;
        if (!renewal) return false;

        if (filters.renewalDue === 'dueSoon') {
          return renewal.daysUntilRenewal <= 30 && renewal.daysUntilRenewal > 0;
        }
        if (filters.renewalDue === 'overdue') {
          return renewal.daysUntilRenewal < 0;
        }
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'completions':
          return (b.certifications?.completed || 0) - (a.certifications?.completed || 0);
        case 'renewal':
          const aRenewal = a.certifications?.earliestRenewal?.daysUntilRenewal ?? Infinity;
          const bRenewal = b.certifications?.earliestRenewal?.daysUntilRenewal ?? Infinity;
          return aRenewal - bRenewal;
        default:
          return 0;
      }
    });

    return filtered;
  }, [data, filters, sortBy, searchTerm]);

  const toggleRow = (teacherId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(teacherId)) {
      newExpanded.delete(teacherId);
    } else {
      newExpanded.add(teacherId);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (teacher) => {
    if (teacher.certifications?.expired > 0) {
      return <span className="badge bg-danger">Expired</span>;
    }
    if (teacher.certifications?.inProgress > 0) {
      return <span className="badge bg-warning">In Progress</span>;
    }
    if (teacher.certifications?.completed > 0) {
      return <span className="badge bg-success">Active</span>;
    }
    return <span className="badge bg-secondary">No Certifications</span>;
  };

  const getRenewalDisplay = (teacher) => {
    const renewal = teacher.certifications?.earliestRenewal;
    if (!renewal) return <span className="text-muted">N/A</span>;

    const days = renewal.daysUntilRenewal;
    if (days < 0) {
      return <span className="text-danger">Overdue ({Math.abs(days)} days)</span>;
    }
    if (days <= 30) {
      return <span className="text-warning fw-bold">{days} days</span>;
    }
    return <span className="text-success">{days} days</span>;
  };

  if (loading) {
    return (
      <div className="main-wrapper">
        {/* Old AdminHeader deleted */}
        <div className="page-content">
          <div className="container">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading dashboard data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-wrapper">
        {/* Old AdminHeader deleted */}
        <div className="page-content">
          <div className="container">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error Loading Dashboard</h4>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="main-wrapper">
        {/* Old AdminHeader deleted */}
        <div className="page-content">
          <div className="container">
            <div className="alert alert-info">No data available</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-wrapper">
      {/* Old AdminHeader deleted */}
      
      <div className="breadcrumb-bar breadcrumb-bar-info">
        <div className="container">
          <div className="row">
            <div className="col-md-12 col-12">
              <div className="breadcrumb-list">
                <h2 className="breadcrumb-title">Teacher Quiz Dashboard</h2>
                <nav aria-label="breadcrumb" className="page-breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/admin/admin-dashboard">Home</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Teacher Quiz Dashboard
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="row">
            <AdminSidebar sidebarVisible={true} />
            
            <div className="col-xl-9 col-lg-9">
              {/* Summary Cards */}
              <div className="row mb-4">
                <div className="col-lg-3 col-md-6 mb-3">
                  <div className="card dash-info">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="me-3" style={{ fontSize: '2.5rem' }}>👥</div>
                        <div>
                          <h5 className="mb-0">Total Teachers</h5>
                          <h2 className="mb-0">{data.summary?.totalTeachers || 0}</h2>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                  <div className="card dash-info">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="me-3" style={{ fontSize: '2.5rem' }}>✅</div>
                        <div>
                          <h5 className="mb-0">With Quiz Completions</h5>
                          <h2 className="mb-0">{data.summary?.teachersWithQuizCompletions || 0}</h2>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                  <div className="card dash-info">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="me-3" style={{ fontSize: '2.5rem' }}>🏆</div>
                        <div>
                          <h5 className="mb-0">With Certifications</h5>
                          <h2 className="mb-0">{data.summary?.teachersWithCertifications || 0}</h2>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                  <div className="card dash-info" style={data.summary?.teachersWithExpiredCertifications > 0 ? { border: '2px solid #dc3545' } : {}}>
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="me-3" style={{ fontSize: '2.5rem' }}>⚠️</div>
                        <div>
                          <h5 className="mb-0">Expired Certifications</h5>
                          <h2 className="mb-0 text-danger">{data.summary?.teachersWithExpiredCertifications || 0}</h2>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters and Search */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, email, or school..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select"
                        value={filters.accountType}
                        onChange={(e) => setFilters({...filters, accountType: e.target.value})}
                      >
                        <option value="all">All Account Types</option>
                        <option value="school">School Teachers</option>
                        <option value="individual">Individual Teachers</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={filters.certificationStatus}
                        onChange={(e) => setFilters({...filters, certificationStatus: e.target.value})}
                      >
                        <option value="all">All Statuses</option>
                        <option value="completed">Has Completed</option>
                        <option value="inProgress">In Progress</option>
                        <option value="expired">Has Expired</option>
                        <option value="none">No Certifications</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="name">Sort by Name</option>
                        <option value="completions">Sort by Completions</option>
                        <option value="renewal">Sort by Renewal Date</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Teachers Table */}
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>School</th>
                          <th>Account Type</th>
                          <th>Quiz Completions</th>
                          <th>Certifications</th>
                          <th>Status</th>
                          <th>Next Renewal</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTeachers.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center py-4">
                              <p className="text-muted mb-0">No teachers found matching your filters.</p>
                            </td>
                          </tr>
                        ) : (
                          filteredTeachers.map(teacher => (
                            <React.Fragment key={teacher._id}>
                              <tr 
                                className="teacher-row" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleRow(teacher._id)}
                              >
                                <td><strong>{teacher.name || 'N/A'}</strong></td>
                                <td>{teacher.email || 'N/A'}</td>
                                <td>{teacher.school?.name || 'N/A'}</td>
                                <td>
                                  <span className={`badge ${teacher.accountType === 'School Teacher' ? 'bg-primary' : 'bg-success'}`}>
                                    {teacher.accountType || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <div>
                                    <strong>{teacher.quizCompletions?.total || 0}</strong> total
                                    <br />
                                    <small className="text-muted">{teacher.quizCompletions?.uniqueQuizzes || 0} unique</small>
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    <strong>{teacher.certifications?.completed || 0}</strong> completed
                                    <br />
                                    <small className="text-muted">
                                      {teacher.certifications?.inProgress > 0 && `${teacher.certifications.inProgress} in progress`}
                                      {teacher.certifications?.expired > 0 && ` • ${teacher.certifications.expired} expired`}
                                    </small>
                                  </div>
                                </td>
                                <td>{getStatusBadge(teacher)}</td>
                                <td>{getRenewalDisplay(teacher)}</td>
                                <td>
                                  <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={(e) => { e.stopPropagation(); toggleRow(teacher._id); }}
                                  >
                                    {expandedRows.has(teacher._id) ? '▼' : '▶'}
                                  </button>
                                </td>
                              </tr>
                              {expandedRows.has(teacher._id) && (
                                <tr className="bg-light">
                                  <td colSpan="9" className="p-4">
                                    <div className="row">
                                      <div className="col-md-6">
                                        <h5>Quiz Completions</h5>
                                        {teacher.quizResults && teacher.quizResults.length > 0 ? (
                                          <ul className="list-group">
                                            {teacher.quizResults.map((quiz, idx) => (
                                              <li key={idx} className="list-group-item">
                                                <strong>{quiz.courseName || 'Unknown Course'}</strong>
                                                <br />
                                                <small className="text-muted">
                                                  Completed: {formatDate(quiz.completionDate)}
                                                  {quiz.expirationDate && (
                                                    <span> • Expires: {formatDate(quiz.expirationDate)}</span>
                                                  )}
                                                </small>
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="text-muted">No quiz completions</p>
                                        )}
                                      </div>
                                      <div className="col-md-6">
                                        <h5>Certifications</h5>
                                        {teacher.certificationDetails && teacher.certificationDetails.length > 0 ? (
                                          <ul className="list-group">
                                            {teacher.certificationDetails.map((cert, idx) => (
                                              <li key={idx} className={`list-group-item ${cert.isExpired ? 'list-group-item-danger' : ''}`}>
                                                <strong>{cert.courseTitle || cert.courseKey || 'Unknown Course'}</strong>
                                                <br />
                                                <span className={`badge ${cert.status === 'completed' ? 'bg-success' : cert.status === 'in_progress' ? 'bg-warning' : 'bg-secondary'}`}>
                                                  {cert.status || 'unknown'}
                                                </span>
                                                {cert.dateCompleted && (
                                                  <small className="text-muted d-block mt-1">
                                                    Completed: {formatDate(cert.dateCompleted)}
                                                  </small>
                                                )}
                                                {cert.renewByDate && (
                                                  <small className={`d-block mt-1 ${cert.isExpired ? 'text-danger fw-bold' : 'text-muted'}`}>
                                                    Renewal: {formatDate(cert.renewByDate)}
                                                    {cert.isExpired && ' (EXPIRED)'}
                                                  </small>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="text-muted">No certifications</p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTeacherQuizDashboard;

